import {
  AccountDockSolicitationStatus,
  CardStatusDock,
  ReasonType,
} from '@portal/models/enumerators/cards';
import { ThunkAction } from 'redux-thunk';
import CardsApi from '~/repositories/cards';

import {
  CLEAR_CARD_DETAILS,
  GET_CARD_DETAILS,
  SET_ACCOUNT_STATUS,
  SET_AVAILABLE_CARDS,
  SET_CARD_ERROR,
  SET_CARD_TRACKING_INFO,
  SET_CARDS,
  SET_HAS_CARDS,
  UNSET_CARD_ERROR,
  UPDATE_CURRENT_CARD,
} from './actionTypes';
import { decreaseLoading, increaseLoading, LoadingAction } from './loading';

export type SetCardErrorAction = {
  type: string;
  payload: Error;
};

export type SetCardsAction = {
  type: string;
  payload: bank.Card[];
};

export type SetHasCardsAction = {
  type: string;
  payload: boolean;
};

export type cancelNoname = {
  cardId: string;
  reason: number;
  description: string;
};

export const setCards = (payload: SetCardsAction['payload']) => ({
  type: SET_CARDS,
  payload,
});

export const getAllCards =
  (
    userId: string,
    load = true,
    callback?: (err: Error | null) => void
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    SetCardsAction | LoadingAction | SetCardErrorAction | SetHasCardsAction
  > =>
  async (dispatch) => {
    if (load) {
      dispatch(increaseLoading());
    }
    try {
      const cards = await CardsApi.get();

      dispatch({
        type: SET_HAS_CARDS,
        payload: cards.length > 0,
      });

      dispatch(setCards(cards));

      dispatch({
        type: UNSET_CARD_ERROR,
      });

      if (callback) {
        callback(null);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      dispatch({
        type: SET_CARD_ERROR,
        payload: error,
      });
      if (callback) {
        callback(error);
      }
    } finally {
      if (load) {
        dispatch(decreaseLoading());
      }
    }
  };

export type AddCardAction = {
  type: string;
  payload: bank.Card;
};

export type SetCardSettingsAction = {
  type: string;
  payload: models.CardSettings;
};

export type UpdateCardAction = {
  type: string;
  payload: {
    id: string;
    card: bank.Card;
  };
};

export const updateCardLimit =
  (
    card: bank.Card,
    userId: string,
    updateCardLimit: number
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetCardErrorAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      if (!card.id) {
        return;
      }
      await CardsApi.updateCardLimit(card.id, updateCardLimit);
      dispatch(getAllCards(userId));
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      dispatch({
        type: SET_CARD_ERROR,
        payload: error,
      });
      dispatch(getAllCards(userId));
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const requestDuplicateCard =
  (
    cardId: string,
    description: ReasonType,
    pin: string,
    callback: (err: Error | null) => void
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetCardErrorAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await CardsApi.requestDuplicate(cardId, description, pin);
      callback(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      callback(error);
    } finally {
      dispatch(decreaseLoading());
    }
  };

export type GetCardDetailsAction = {
  type: string;
  payload: bank.Card;
};

export const clearCardDetails = () => (dispatch) => {
  dispatch({ type: CLEAR_CARD_DETAILS });
};

export const getCardDetails = (cardId: string) => async (dispatch) => {
  dispatch(increaseLoading());
  try {
    const response = await CardsApi.getCardDetails(cardId);

    dispatch({
      type: GET_CARD_DETAILS,
      payload: response,
    });
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));
    dispatch({
      type: SET_CARD_ERROR,
      payload: error,
    });
  } finally {
    dispatch(decreaseLoading());
  }
};

export type SetAvailableCardsAction = {
  type: string;
  payload: models.AvailableCards;
};

export const setAvailableCards = (
  payload: SetAvailableCardsAction['payload']
) => ({
  type: SET_AVAILABLE_CARDS,
  payload,
});

export const unsetCardError = () => ({
  type: UNSET_CARD_ERROR,
});

export const updateCurrentCard = (payload: AddCardAction['payload']) => ({
  type: UPDATE_CURRENT_CARD,
  payload,
});

export const blockCardV3 =
  (
    cardId: string,
    status: CardStatusDock,
    pin: string,
    callback: (err: Error | null) => void
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetCardErrorAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await CardsApi.blockCardV3(cardId, status, pin);

      callback(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      callback(error);
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const unblockCardV3 =
  (
    cardId: string,
    pin: string,
    callback: (err: Error | null) => void
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetCardErrorAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await CardsApi.unblockCardV3(cardId, pin);

      callback(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      callback(error);
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const changePasswordV3 =
  (
    cardId: string,
    newPassword: string,
    isUnlockedByIncorrectPassword: boolean,
    pin: string,
    callback: (err: Error | null) => void
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetCardErrorAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await CardsApi.changePasswordV3(
        cardId,
        newPassword,
        isUnlockedByIncorrectPassword,
        pin
      );

      callback(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      callback(error);
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const cancelCardV3 =
  (
    cardId: string,
    description: ReasonType,
    pin: string,
    callback: (err: Error | null) => void
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetCardErrorAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await CardsApi.cancelCardV3(cardId, description, pin);

      callback(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      callback(error);
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const viewPasswordV3 =
  (
    cardId: string,
    pin: string,
    callback: (err: Error | null, password?: string) => void
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetCardErrorAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      const { password } = await CardsApi.viewPasswordV3(cardId, pin);
      callback(null, password);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      callback(error);
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const createVirtualCardV3 =
  (
    expirationDate: string,
    pin: string,
    callback: (err: Error | null) => void
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetCardErrorAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await CardsApi.createVirtualCardV3(expirationDate, pin);

      callback(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      callback(error);
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const createPhysicalCardV3 =
  (
    pin: string,
    callback: (err: Error | null) => void
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetCardErrorAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await CardsApi.createPhysicalCardV3(pin);

      callback(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      callback(error);
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const viewCardDataV3 =
  (
    cardId: string,
    pin: string,
    callback: (err: Error | null, data?: bank.Card) => void
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetCardErrorAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      const response = await CardsApi.viewCardDataV3(cardId, pin);
      callback(null, response);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      callback(error);
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const unlockFirstAccessV3 =
  (
    cardId: string,
    currentCardPassword: string,
    newCardPassword: string,
    pin: string,
    callback: (err: Error | null) => void
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetCardErrorAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await CardsApi.unlockFirstAccessV3(
        cardId,
        currentCardPassword,
        newCardPassword,
        pin
      );

      callback(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      callback(error);
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const unlockForIncorrectPasswordV3 =
  (
    cardId: string,
    pin: string,
    callback: (err: Error | null) => void
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetCardErrorAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await CardsApi.unlockForIncorrectPasswordV3(cardId, pin);

      callback(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      callback(error);
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const cardControlV3 =
  (
    cardId: string,
    pin: string,
    cardControl: bank.CardControl,
    callback: (err: Error | null) => void
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetCardErrorAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await CardsApi.cardControlV3(cardId, pin, cardControl);

      callback(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      callback(error);
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const verifyAccountDock =
  (
    callback: (
      err: Error | null,
      status: AccountDockSolicitationStatus | null
    ) => void
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetCardErrorAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      const response = await CardsApi.verifyAccountDock();

      dispatch({
        type: SET_ACCOUNT_STATUS,
        payload: response,
      });

      callback(null, response?.status);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      callback(error, null);
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const createAccountDock =
  (
    callback: (err: Error | null) => void
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetCardErrorAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await CardsApi.createAccountDock();

      callback(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      callback(error);
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const trackCard =
  (
    cardId: string,
    callback: (err: Error | null) => void
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetCardErrorAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      const response = await CardsApi.trackCard(cardId);

      dispatch({
        type: SET_CARD_TRACKING_INFO,
        payload: response,
      });
      callback(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      callback(error);
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const signatureHash =
  (documentUrl: string, callback: (err: Error | null) => void) =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await CardsApi.createSignatureHash(documentUrl);
      callback(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      callback(error);
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const confirmCardReceived =
  (
    cardId: string,
    callback: (err: Error | null) => void
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetCardErrorAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await CardsApi.confirmCardReceived(cardId);
      callback(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      callback(error);
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const validateOwnerShip =
  (
    cardId: string,
    callback: (err: Error | null) => void
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | SetCardErrorAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      await CardsApi.validateOwnerShip(cardId);
      callback(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      callback(error);
    } finally {
      dispatch(decreaseLoading());
    }
  };
